<div class="booking-auth-overlay" (click)="handleOverlayClick()">
  @let dialogTexts = texts();
  <section
    class="booking-auth-dialog"
    [attr.role]="isDialog() ? 'dialog' : 'region'"
    [attr.aria-modal]="isDialog() ? 'true' : null"
    aria-labelledby="booking-auth-title"
    (click)="$event.stopPropagation()"
  >
    <header class="booking-auth-header">
      <h2 id="booking-auth-title">
        {{ dialogTitle() }}
      </h2>
      @if (isDialog()) {
        <button
          class="booking-auth-close"
          type="button"
          [attr.aria-label]="dialogTexts.Close"
          (click)="closeDialog()"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M18.3 5.7 5.7 18.3M5.7 5.7l12.6 12.6" />
          </svg>
        </button>
      }
    </header>

    @if (showAccountModeTabs()) {
      <div
        class="booking-auth-tabs"
        role="tablist"
        [attr.aria-label]="dialogTexts.AccountMode"
      >
        <button
          type="button"
          role="tab"
          [attr.aria-selected]="mode() === 'login'"
          [class.active]="mode() === 'login'"
          [disabled]="isSubmitting()"
          (click)="switchMode('login')"
        >
          {{ dialogTexts.Login }}
        </button>
        <button
          type="button"
          role="tab"
          [attr.aria-selected]="mode() === 'register'"
          [class.active]="mode() === 'register'"
          [disabled]="isSubmitting()"
          (click)="switchMode('register')"
        >
          {{ dialogTexts.CreateAccount }}
        </button>
      </div>
    }

    @if (errorMessageKey(); as errorKey) {
      <p class="booking-auth-error" role="alert">
        {{ getDialogErrorMessage(errorKey) }}
      </p>
    }

    @if (passwordResetCompleted()) {
      <p class="booking-auth-success" role="status">
        {{ dialogTexts.PasswordReset }}
      </p>
    }

    @if (passwordResetEmailSent()) {
      <p class="booking-auth-success" role="status">
        {{ dialogTexts.PasswordResetEmailSent }}
      </p>
    }

    @if (mode() === "login") {
      <form [formGroup]="loginForm" (ngSubmit)="submitLogin()">
        <label>
          {{ dialogTexts.Email }}
          <input type="email" formControlName="email" autocomplete="email" />
        </label>
        <label>
          {{ dialogTexts.Password }}
          <input
            type="password"
            formControlName="password"
            autocomplete="current-password"
          />
        </label>
        <button class="btn" type="submit" [disabled]="isSubmitting()">
          {{ dialogTexts.Login }}
        </button>
        <button
          class="booking-auth-text-action"
          type="button"
          [disabled]="isSubmitting()"
          (click)="startForgotPassword()"
        >
          {{ dialogTexts.ForgotPassword }}
        </button>
      </form>
    } @else if (mode() === "register") {
      <form [formGroup]="registerForm" (ngSubmit)="submitRegister()">
        <label>
          {{ dialogTexts.FirstName }}
          <input
            type="text"
            formControlName="firstName"
            autocomplete="given-name"
          />
        </label>
        <label>
          {{ dialogTexts.LastName }}
          <input
            type="text"
            formControlName="lastName"
            autocomplete="family-name"
          />
        </label>
        <label>
          {{ dialogTexts.Email }}
          <input type="email" formControlName="email" autocomplete="email" />
        </label>
        <label>
          {{ dialogTexts.MobilePhone }}
          <input
            type="tel"
            formControlName="mobilePhoneNumber"
            autocomplete="tel"
          />
        </label>
        <label>
          {{ dialogTexts.Password }}
          <input
            type="password"
            formControlName="password"
            autocomplete="new-password"
          />
        </label>
        <button class="btn" type="submit" [disabled]="isSubmitting()">
          {{ dialogTexts.CreateAccount }}
        </button>
      </form>
    } @else if (mode() === "forgotPassword") {
      <p class="booking-auth-instructions">
        {{ dialogTexts.ForgotPasswordIntro }}
      </p>
      <form [formGroup]="forgotPasswordForm" (ngSubmit)="submitForgotPassword()">
        <label>
          {{ dialogTexts.Email }}
          <input type="email" formControlName="email" autocomplete="email" />
        </label>
        <button class="btn" type="submit" [disabled]="isSubmitting()">
          {{ dialogTexts.SendPasswordResetCode }}
        </button>
        <button
          class="btn booking-auth-secondary-action"
          type="button"
          [disabled]="isSubmitting()"
          (click)="returnToLogin()"
        >
          {{ dialogTexts.BackToLogin }}
        </button>
      </form>
    } @else if (mode() === "verifyResetCode") {
      <p class="booking-auth-instructions">
        {{ dialogTexts.PasswordResetCodeIntro }}
      </p>
      <form
        [formGroup]="passwordResetCodeForm"
        (ngSubmit)="submitPasswordResetCode()"
      >
        <label>
          {{ dialogTexts.PasswordResetCode }}
          <input
            type="text"
            inputmode="numeric"
            maxlength="6"
            formControlName="code"
            autocomplete="one-time-code"
          />
        </label>
        <button class="btn" type="submit" [disabled]="isSubmitting()">
          {{ dialogTexts.VerifyPasswordResetCode }}
        </button>
        <button
          class="btn booking-auth-secondary-action"
          type="button"
          [disabled]="isSubmitting()"
          (click)="returnToLogin()"
        >
          {{ dialogTexts.BackToLogin }}
        </button>
      </form>
    } @else if (mode() === "resetPassword") {
      <form [formGroup]="resetPasswordForm" (ngSubmit)="submitResetPassword()">
        <label>
          {{ dialogTexts.NewPassword }}
          <input
            type="password"
            formControlName="newPassword"
            autocomplete="new-password"
          />
        </label>
        <button class="btn" type="submit" [disabled]="isSubmitting()">
          {{ dialogTexts.ResetPassword }}
        </button>
        <button
          class="btn booking-auth-secondary-action"
          type="button"
          [disabled]="isSubmitting()"
          (click)="returnToLogin()"
        >
          {{ dialogTexts.BackToLogin }}
        </button>
      </form>
    } @else {
      <p class="booking-auth-instructions">
        {{ dialogTexts.ValidateEmailIntro }}
      </p>
      @if (validationEmailSent()) {
        <p class="booking-auth-success" role="status">
          {{ dialogTexts.ValidationEmailSent }}
        </p>
      }
      <form
        [formGroup]="validationCodeForm"
        (ngSubmit)="submitValidationCode()"
      >
        <label>
          {{ dialogTexts.ValidationCode }}
          <input
            type="text"
            inputmode="numeric"
            maxlength="4"
            formControlName="code"
            autocomplete="one-time-code"
          />
        </label>
        <button class="btn" type="submit" [disabled]="isBusy()">
          {{ dialogTexts.ValidateEmail }}
        </button>
        <button
          class="btn booking-auth-secondary-action"
          type="button"
          [disabled]="isBusy()"
          (click)="resendValidationEmail()"
        >
          {{ dialogTexts.ResendValidationEmail }}
        </button>
      </form>
    }
  </section>
</div>
